import React, { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import * as pdfjs from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const thumbCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

const renderPdfThumb = async (url: string): Promise<string> => {
    const pdf = await pdfjs.getDocument({ url }).promise;
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(500 / baseViewport.width, 500 / baseViewport.height);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D non disponibile');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL('image/jpeg', 0.7);
};

interface Props {
    cacheKey: string;
    getUrl: () => Promise<string>;
}

export const PdfThumb: React.FC<Props> = ({ cacheKey, getUrl }) => {
    const cached = thumbCache.get(cacheKey);
    const [thumb, setThumb] = useState<string | null>(cached || null);
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (thumbCache.has(cacheKey)) {
            setThumb(thumbCache.get(cacheKey)!);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(false);

        const promise = inflight.get(cacheKey) ?? (async () => {
            const url = await getUrl();
            const dataUrl = await renderPdfThumb(url);
            thumbCache.set(cacheKey, dataUrl);
            return dataUrl;
        })().finally(() => inflight.delete(cacheKey));
        inflight.set(cacheKey, promise);

        promise.then(dataUrl => {
            if (!cancelled) { setThumb(dataUrl); setLoading(false); }
        }).catch(() => {
            if (!cancelled) { setError(true); setLoading(false); }
        });

        return () => { cancelled = true; };
    }, [cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-slate-100">
                <Loader2 size={24} className="text-slate-400 animate-spin" />
            </div>
        );
    }
    if (error || !thumb) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 gap-1">
                <FileText size={36} className="text-red-400" />
                <span className="text-[9px] font-black text-red-400 uppercase tracking-widest">PDF</span>
            </div>
        );
    }
    return <img src={thumb} alt="PDF" className="w-full h-full object-contain bg-white" />;
};
